<template>
  <div class="flex flex-col gap-4">
    <div v-if="self && self.texts.length > 0" class="flex flex-row justify-end">
      <ul class="flex flex-col gap-0.5">
        <li v-for="[date, texts] in Object.entries(selfGroupedByDate)" :key="date">
          <div
            v-if="date !== 'none'"
            class="text-label-small text-on-surface-variant w-full px-2 text-right font-normal"
          >
            {{ useDisplayDate(date, { dayName: 'none', monthName: 'short' }).value }}
          </div>
          <ul class="ml-10 flex flex-col gap-0.5">
            <li
              v-for="text in texts"
              :key="text.id"
              class="bg-secondary-container text-on-secondary-container rounded-large rounded-br-extra-small last:rounded-br-large rounded-tr-extra-small first:rounded-tr-large mr-1 ml-auto px-3 py-2"
              :class="[date == 'none' && 'mt-4']"
            >
              <WysiwygTextFieldReadOnly :value="text.text" />
            </li>
          </ul>
        </li>
      </ul>
      <BaseMemberAvatar v-if="selfMember" size="32" class="mt-4" :member="selfMember" />
      <div
        v-else
        v-tooltip="$t('perspectives.self')"
        class="bg-primary-container text-on-primary-container mt-4 flex h-8 w-8 shrink-0 items-center justify-center rounded-full"
      >
        <BaseIcon size="20">{{ iconForPerspective(SELF_PERSPECTIVE_KEY) }}</BaseIcon>
      </div>
    </div>
    <div v-for="entry in otherPerspectives" :key="analysisPerspectiveKey(entry)" class="flex flex-row">
      <ul class="flex flex-col gap-0.5">
        <li
          v-for="[senderKey, textsForSender] in Object.entries(groupedByMemberAndDate(entry))"
          :key="senderKey"
          class="flex flex-row"
        >
          <div class="mt-4">
            <BaseMemberAvatar
              v-if="textsForSender.sender"
              size="32"
              :member="textsForSender.sender"
            />
            <div
              v-else
              v-tooltip="perspectiveLabel(analysisPerspectiveKey(entry) ?? '')"
              class="bg-primary-container text-on-primary-container flex h-8 w-8 shrink-0 items-center justify-center rounded-full"
            >
              <BaseIcon size="20">{{
                iconForPerspective(
                  resolveAnalysisPerspective(entry) ?? analysisPerspectiveKey(entry) ?? '',
                )
              }}</BaseIcon>
            </div>
          </div>
          <ul class="flex flex-col gap-0.5">
            <li v-for="[date, texts] in Object.entries(textsForSender.texts)" :key="date">
              <div
                v-if="date !== 'none' || textsForSender.sender"
                class="text-label-small text-on-surface-variant w-full px-2 font-normal"
              >
                <span>{{
                  textsForSender.sender ? memberFullName(textsForSender.sender) : ''
                }}</span>
                <span v-if="date !== 'none' && textsForSender.sender">・</span>
                <span>{{
                  useDisplayDate(date, { dayName: 'none', monthName: 'short' }).value
                }}</span>
              </div>
              <ul class="flex flex-col gap-0.5">
                <li
                  v-for="text in texts"
                  :key="text.id"
                  class="bg-surface-container-highest rounded-large rounded-bl-extra-small last:rounded-bl-large rounded-tl-extra-small first:rounded-tl-large mr-10 ml-1 px-3 py-2"
                  :class="[date == 'none' && !textsForSender.sender && 'mt-4']"
                >
                  <WysiwygTextFieldReadOnly :value="text.text" />
                </li>
              </ul>
            </li>
          </ul>
        </li>
      </ul>
    </div>
  </div>
</template>
<script lang="ts" setup>
  import BaseMemberAvatar from '../../../../core/components/base/avatar/BaseMemberAvatar.vue'
  import WysiwygTextFieldReadOnly from '../../../../core/components/base/wysiwyg/WysiwygTextFieldReadOnly.vue'
  import { computed } from 'vue'
  import BaseIcon from '../../base/icon/BaseIcon.vue'
  import type { TextFeedback, TextPerspectiveAnalysis } from '@/core/models/surveys-analytics'
  import { useDisplayDate } from '@/core/composables/use-display-date'
  import type { Member } from '@/core/models/member/member'
  import { memberFullName } from '@/core/models/member/member-name'
  import { iconForPerspective } from '@/core/models/feedback-perspective'
  import { selfEntry, nonSelfEntries, analysisPerspectiveKey, resolveAnalysisPerspective } from '@/core/models/perspective-analysis'
  import { SELF_PERSPECTIVE_KEY, type Perspective } from '@/core/models/perspective'
  import { resolvePerspectiveLabel } from '@/core/models/perspective-label'
  import { useI18n } from 'vue-i18n'

  interface Props {
    perspectives: ReadonlyArray<TextPerspectiveAnalysis>
  }

  const props = defineProps<Props>()
  const { t } = useI18n()

  const self = computed(() => selfEntry(props.perspectives))
  const otherPerspectives = computed(() =>
    nonSelfEntries(props.perspectives).filter((entry) => entry.texts.length > 0),
  )

  const selfMember = computed(() => self.value?.texts[0]?.sender ?? null)

  const perspectiveLabel = (key: string) => {
    const catalog = props.perspectives
      .map((entry) => resolveAnalysisPerspective(entry))
      .filter((p): p is Perspective => p != null)
    return resolvePerspectiveLabel(key, catalog, { selfLabel: t('perspectives.self') })
  }

  const selfGroupedByDate = computed(() => {
    const texts = self.value?.texts ?? []
    return texts.reduce<Record<string, TextFeedback[]>>((acc, text) => {
      const date = text.date ? new Date(text.date).toISOString().split('T')[0] : 'none'
      if (!acc[date]) {
        acc[date] = []
      }
      acc[date].push({ ...text })
      return acc
    }, {})
  })

  // Group texts first by sender (senders without an id are treated as anonymous), then by date
  const groupedByMemberAndDate = (entry: TextPerspectiveAnalysis) => {
    return entry.texts.reduce<
      Record<
        string,
        {
          sender: Member | null
          texts: Record<string, TextFeedback[]>
        }
      >
    >((acc, text, index) => {
      const senderKey = text.sender?.id ?? `anonymous-${index}`
      const date = text.date ? new Date(text.date).toISOString().split('T')[0] : 'none'
      if (!acc[senderKey]) {
        acc[senderKey] = {
          sender: text.sender ?? null,
          texts: {},
        }
      }
      if (!acc[senderKey]!.texts[date]) {
        acc[senderKey]!.texts[date] = []
      }
      acc[senderKey]!.texts[date].push({ ...text })
      return acc
    }, {})
  }
</script>
